import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Grid, Typography, Alert, LinearProgress, CircularProgress, Divider
} from "@mui/material";
import { Edit, Delete, RocketLaunch, Pause, PlayArrow, Stop, Refresh, History } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  aiCallApi, useAICallOverview, useMarketingPresets, useInterval, formatBdt, formatNumber, dhaka,
  presetLabel, presetOptionText, LANDINGS
} from "./useAICalling";
import {
  AICallingPage, SectionCard, Loading, ErrorState, CampaignStatusChip, ConfirmDialog, KeyValue, Step
} from "./AICallingComponents";
import { CampaignReport } from "./AICallReportView";
import AICallCampaignForm from "./AICallCampaignForm";
import SignupFollowUpsCard from "./SignupFollowUpsCard";

const PROGRESS_KEYS = ["queued", "calling", "sent", "failed", "skipped"];

const AICallingCampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { overview, fetch: fetchOverview } = useAICallOverview();
  const { presets, fetch: fetchPresets } = useMarketingPresets();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState(null); // "launch" | "stop" | "delete"
  const [launchPreview, setLaunchPreview] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      const res = await aiCallApi.campaign(campaignId);
      if (res.ok) {
        setDetail(res.data);
        setError("");
        setUpdatedAt(new Date());
      } else if (!quiet) {
        setError(res.error);
      }
      setLoading(false);
      return res;
    },
    [campaignId]
  );

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);
  useEffect(() => {
    fetchOverview({ quiet: true });
    fetchPresets();
  }, [fetchOverview, fetchPresets]);

  const campaign = detail?.campaign;
  const status = campaign?.status;
  const running = status === "running";
  useInterval(() => load(true), 15000, running);

  const act = async (action) => {
    setActing(true);
    const res = await aiCallApi.campaignAction(campaignId, action);
    setActing(false);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Done");
    await load(true);
    return true;
  };

  const remove = async () => {
    const res = await aiCallApi.deleteCampaign(campaignId);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Campaign deleted");
    navigate("/ai-calling/campaigns");
    return true;
  };

  const openLaunch = async () => {
    setLaunchPreview(null);
    setConfirm("launch");
    const c = campaign;
    const res = await aiCallApi.previewCampaign({ audience: c.audience, holdoutPercent: c.holdoutPercent, pilot: c.pilot });
    setLaunchPreview(res.ok ? res.data : { error: res.error });
  };

  const back = { to: "/ai-calling/campaigns", label: "Campaigns" };

  if (loading && !detail) return <AICallingPage title="Campaign" back={back}><Loading /></AICallingPage>;
  if (!campaign) {
    return (
      <AICallingPage title="Campaign" back={back}>
        <ErrorState error={error || "Campaign not found"} onRetry={() => load()} />
      </AICallingPage>
    );
  }

  const isLadder = campaign.system === "ladder";
  const isNotice = campaign.system === "notice";
  const isSignup = campaign.system === "signup";
  const isDraft = status === "draft";
  const progress = detail.progress || {};
  const sms = detail.sms || {};
  const groups = detail.groups || {};
  const stats = campaign.stats || {};
  const pilotOn = !!campaign.pilot?.enabled;
  const assigned = PROGRESS_KEYS.reduce((s, k) => s + (Number(progress[k]) || 0), 0);
  const done = (Number(progress.sent) || 0) + (Number(progress.failed) || 0) + (Number(progress.skipped) || 0);
  const donePct = assigned ? Math.round((done / assigned) * 100) : 0;
  const settings = overview?.settings;
  const ownWindows = campaign.callWindows?.length ? campaign.callWindows : null;
  const windows = (ownWindows || settings?.limits?.callWindows || []).map((w) => `${w.start}–${w.end}`).join(", ");
  const smsCost = Number(stats.smsCostBdt) || 0;

  const audience = campaign.audience || {};
  const options = Object.entries(audience.presetOptions || {})
    .map(([k, v]) => `${presetOptionText(audience.preset, k).label}: ${v}`)
    .join(" · ");
  const pickedIds = audience.selectedUserIds || [];

  const actions = (
    <>
      {isDraft && !isLadder && (
        <>
          <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)} disabled={acting}>Edit</Button>
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => setConfirm("delete")} disabled={acting}>Delete</Button>
          <Button variant="contained" color="success" startIcon={<RocketLaunch />} onClick={openLaunch} disabled={acting}>Launch</Button>
        </>
      )}
      {running && (
        <Button variant="outlined" startIcon={acting ? <CircularProgress size={16} /> : <Pause />} onClick={() => act("pause")} disabled={acting}>
          Pause
        </Button>
      )}
      {status === "paused" && (
        <Button variant="contained" startIcon={acting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />} onClick={() => act("resume")} disabled={acting}>
          Resume
        </Button>
      )}
      {!isLadder && !isNotice && !isSignup && ["running", "paused", "scheduled"].includes(status) && (
        <Button variant="outlined" color="error" startIcon={<Stop />} onClick={() => setConfirm("stop")} disabled={acting}>Stop</Button>
      )}
      <Button startIcon={<Refresh />} onClick={() => load()} disabled={loading}>Refresh</Button>
    </>
  );

  return (
    <AICallingPage title={campaign.name} subtitle={campaign.description || undefined} back={back} actions={actions}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <CampaignStatusChip status={status} size="medium" />
        {isLadder && <Chip variant="outlined" label="Channel ladder (automatic)" />}
        {isNotice && <Chip variant="outlined" label="Purchase notice (automatic)" />}
        {isSignup && <Chip variant="outlined" label="New signups (automatic)" />}
        {pilotOn && <Chip variant="outlined" color="secondary" label="Pilot" />}
        {running && updatedAt && (
          <Typography variant="caption" color="text.secondary">Updates every 15 s · last {updatedAt.toLocaleTimeString("en-US", { timeZone: "Asia/Dhaka" })}</Typography>
        )}
      </Box>

      {isLadder && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The channel ladder adds members to this campaign by itself. You can pause it; the ladder may start it again when it queues the next call.
        </Alert>
      )}
      {isNotice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Members who try to buy in the app and get the payment-link SMS are added here by themselves. The AI rings once, says the
          packages and wallet are bought only at app.bibahobangla.com and the link is in their SMS, then hangs up. A missed call is
          not tried again. Pause to stop these calls; the SMS still goes out.
        </Alert>
      )}
      {isSignup && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Every new member with a verified number is added here about 1 hour after signing up (checked every 10 minutes).
          The AI calls {windows || "09:00–22:00"} (Bangladesh time): it welcomes them, reminds them to add a photo and
          biodata if missing, and offers a contact or package, then texts the payment link. Members who already have a
          package are skipped, and the AI-call rules in Settings still apply. Calls not made within 3 days are dropped.
          Pause to stop new calls.
        </Alert>
      )}
      {settings && !settings.enabled && ["running", "draft", "paused"].includes(status) && (
        <Alert severity="warning" sx={{ mb: 2 }}>AI calling is switched off, so nobody is called until it is switched on (AI Calling overview).</Alert>
      )}
      {overview?.engine && !overview.engine.ok && running && (
        <Alert severity="warning" sx={{ mb: 2 }}>The calling process isn't running, so calls are waiting.</Alert>
      )}

      {isSignup && <SignupFollowUpsCard campaignId={campaignId} signup={detail.signup} onSaved={() => load(true)} />}

      <SectionCard title="Settings">
        <Grid container spacing={2}>
          <KeyValue label="Audience">
            {isLadder
              ? "Members queued by the channel ladder"
              : isNotice
                ? "Members texted the payment link after a purchase attempt in the app"
                : isSignup
                ? "New members, about 1 hour after signup"
                : audience.preset
                ? presetLabel(presets, audience.preset)
                : `${formatNumber(pickedIds.length)} hand-picked member${pickedIds.length === 1 ? "" : "s"}`}
            {options && <Typography variant="caption" color="text.secondary" display="block">{options}</Typography>}
          </KeyValue>
          <KeyValue label="Audience size">{audience.size ? formatNumber(audience.size) : isDraft ? "Counted at launch" : "0"}</KeyValue>
          <KeyValue label="Holdout">{pilotOn ? "Pilot: three equal groups" : `${campaign.holdoutPercent ?? 0}%`}</KeyValue>
          <KeyValue label="Count payments within">{campaign.attributionDays ? `${campaign.attributionDays} days` : ""}</KeyValue>
          <KeyValue label="Payment link opens">{LANDINGS[campaign.landing] || campaign.landing}</KeyValue>
          <KeyValue label="Don't call before">{campaign.startAt ? dhaka(campaign.startAt) : ""}</KeyValue>
          <KeyValue label="Created">{`${dhaka(campaign.createdAt)}${campaign.createdByName ? ` by ${campaign.createdByName}` : ""}`}</KeyValue>
          <KeyValue label="Launched">{campaign.launchedAt ? dhaka(campaign.launchedAt) : ""}</KeyValue>
          <KeyValue label="Finished">{campaign.completedAt ? dhaka(campaign.completedAt) : ""}</KeyValue>
        </Grid>
        {pilotOn && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Pilot SMS</Typography>
            <Box sx={{ bgcolor: "grey.100", p: 1.5, borderRadius: 1, mt: 0.5 }} lang="bn">
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{campaign.pilot.smsText}</Typography>
            </Box>
          </Box>
        )}
        {!audience.preset && pickedIds.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, wordBreak: "break-all" }}>
            Member IDs: {pickedIds.slice(0, 5).join(", ")}{pickedIds.length > 5 ? ` and ${formatNumber(pickedIds.length - 5)} more` : ""}
          </Typography>
        )}
      </SectionCard>

      {!isDraft && (
        <SectionCard
          title="Progress"
          action={<Button size="small" startIcon={<History />} onClick={() => navigate(`/ai-calling/calls?campaignId=${campaignId}`)}>See the calls</Button>}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Step label="Waiting" value={Number(progress.queued) || 0} />
            <Step label="Calling now" value={Number(progress.calling) || 0} />
            <Step label="Answered" value={Number(progress.sent) || 0} />
            <Step label="Not answered / failed" value={Number(progress.failed) || 0} />
            <Step label="Skipped" value={Number(progress.skipped) || 0} />
            <Step label="Holdout" value={Number(progress.holdout) || 0} sub="Never called" />
          </Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {formatNumber(done)} of {formatNumber(assigned)} done ({donePct}%)
          </Typography>
          <LinearProgress variant="determinate" value={donePct} sx={{ height: 8, borderRadius: 4, mb: 2 }} />
          <Grid container spacing={2}>
            <KeyValue label="Dialed" md={3}>{formatNumber(stats.dialed)}</KeyValue>
            <KeyValue label="Answered" md={3}>{formatNumber(stats.answered)}</KeyValue>
            <KeyValue label="Payment link sent" md={3}>{formatNumber(stats.linkSent)}</KeyValue>
            <KeyValue label="Cost" md={3}>
              {formatBdt((Number(stats.costBdt) || 0) + smsCost)}
              {smsCost > 0 && <Typography variant="caption" color="text.secondary" display="block">Calls {formatBdt(stats.costBdt)} + SMS {formatBdt(smsCost)}</Typography>}
            </KeyValue>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Waiting members are called inside the call windows{windows ? ` (${windows})` : ""}. Not answered: tried again up to the retry limit.
            Skipped: a safety rule stopped the call (do-not-call, called recently, paid since, …) or the campaign was stopped.
          </Typography>
        </SectionCard>
      )}

      {!isDraft && pilotOn && (
        <SectionCard title="Pilot groups" subtitle="Random, equal groups compared on the results below">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Step label="Holdout (nothing)" value={Number(progress.holdout ?? groups.holdout) || 0} />
            <Step label="SMS only" value={Number(groups.sms) || 0} />
            <Step label="SMS + AI call" value={assigned} />
          </Box>
          <Typography variant="subtitle2" gutterBottom>Pilot SMS</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.keys(sms).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No SMS rows yet.</Typography>
            ) : (
              Object.entries(sms).map(([k, n]) => (
                <Chip key={k} size="small" variant="outlined" color={k === "failed" ? "error" : k === "sent" || k === "delivered" ? "success" : "default"}
                  label={`${k.charAt(0).toUpperCase() + k.slice(1)}: ${formatNumber(n)}`} />
              ))
            )}
            {smsCost > 0 && <Chip size="small" label={`SMS cost ${formatBdt(smsCost)}`} />}
          </Box>
        </SectionCard>
      )}

      {!isDraft && <CampaignReport campaignId={campaignId} refreshMs={running ? 60000 : 0} />}

      {isDraft && (
        <Alert severity="info">
          This is a draft. Nothing happens until you launch it. Use Edit to change it, or Launch to start calling.
        </Alert>
      )}

      <ConfirmDialog
        open={confirm === "launch"}
        title="Launch this campaign?"
        confirmText="Launch"
        confirmColor="success"
        disabled={!launchPreview}
        onClose={() => setConfirm(null)}
        onConfirm={() => act("launch")}
      >
        <Typography gutterBottom>
          This calls real members with the AI agent, only inside the call windows{windows ? ` (${windows}, Bangladesh time)` : ""}
          {settings?.limits?.dailyBudgetBdt !== undefined ? ` and up to the daily budget of ${formatBdt(settings.limits.dailyBudgetBdt)}` : ""}.
          {campaign.startAt ? ` Calls start after ${dhaka(campaign.startAt)}.` : ""}
        </Typography>
        {pilotOn && (
          <Typography gutterBottom>
            Pilot: two of the three groups get the SMS at launch (or in the next SMS send window); the SMS + AI call group is called at least 2 hours later.
          </Typography>
        )}
        {!launchPreview ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <CircularProgress size={18} /><Typography variant="body2">Counting members…</Typography>
          </Box>
        ) : launchPreview.error ? (
          <Alert severity="warning" sx={{ mt: 1 }}>Couldn't count the audience: {launchPreview.error}</Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 1 }}>
            About <b>{formatNumber(launchPreview.toCall)}</b> of {formatNumber(launchPreview.members)} members will be called
            ({formatNumber(launchPreview.women)} women in the audience) · at most {formatBdt(launchPreview.maxBdt)} if everyone answers.
          </Alert>
        )}
        {launchPreview && !launchPreview.error && launchPreview.members === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>Nobody matches this audience right now.</Alert>
        )}
        {settings && !settings.enabled && (
          <Alert severity="warning" sx={{ mt: 1 }}>AI calling is switched off: nobody is called until it is switched on.</Alert>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === "stop"}
        title="Stop this campaign?"
        confirmText="Stop"
        confirmColor="error"
        onClose={() => setConfirm(null)}
        onConfirm={() => act("stop")}
      >
        Members still waiting won't be called. Calls in progress finish. A stopped campaign can't be started again.
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === "delete"}
        title="Delete this draft?"
        confirmText="Delete"
        confirmColor="error"
        onClose={() => setConfirm(null)}
        onConfirm={remove}
      >
        {`"${campaign.name}" will be deleted.`}
      </ConfirmDialog>

      <AICallCampaignForm
        open={editOpen}
        campaign={campaign}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          load(true);
        }}
      />
    </AICallingPage>
  );
};

export default AICallingCampaignDetail;
